import React, { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSButton from "../../system-design/DSButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTypography from "../../system-design/DSTypography";
import DSAvatar from "../../system-design/DSAvatar";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import useApprovedCollectionsInfiniteQuery from "../../Collection/hooks/usePublicCollectionsInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../Device/hooks/useListboxInfiniteScroll";
import { PopulatedApprovedCollection } from "../../../api/collection";

interface CollectionPickDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (collectionId: string) => void;
}

const CollectionPickDialog = ({
  open,
  onClose,
  onPick,
}: CollectionPickDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCollection, setSelectedCollection] =
    useState<PopulatedApprovedCollection | null>(null);

  const {
    data: collections,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useApprovedCollectionsInfiniteQuery(searchQuery, 10, false);

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const handlePick = () => {
    if (!selectedCollection) return;
    onPick(selectedCollection.id);
    setSelectedCollection(null);
    setSearchQuery("");
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>Choose Affiliate Collection</DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ pt: 1 }}>
          <Autocomplete
            options={collections || []}
            filterOptions={noClientFilter}
            getOptionLabel={(option) => option.name || option.id}
            value={selectedCollection}
            onChange={(_, newValue) => setSelectedCollection(newValue)}
            inputValue={searchQuery}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            loading={isLoading || isFetchingNextPage}
            ListboxProps={listboxProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search collections..."
                variant="outlined"
                size="small"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading && <DSCircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSAvatar
                    src={option.coverUrl}
                    alt={option.name}
                    sx={{ width: 32, height: 32 }}
                  />
                  <DSBox>
                    <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                      {option.name}
                    </DSTypography>
                    <DSTypography variant="caption" color="text.secondary">
                      {option.id}
                    </DSTypography>
                  </DSBox>
                </DSBox>
              </li>
            )}
            noOptionsText={
              searchQuery ? "No collections found" : "Type to search"
            }
          />
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Cancel</DSSecondaryButton>
        <DSButton
          variant="contained"
          onClick={handlePick}
          disabled={!selectedCollection}
        >
          Select
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default CollectionPickDialog;
